/**
 * Shared DEV_ACTOR_MAP for mock auth mode.
 *
 * DRR-201: All roles MUST match role-registry.yaml IDs.
 * Run `ferp-seed validate-auth-chain --check-dev-parity` to verify.
 *
 * This file is dev-only — production resolves actors via Authentik + role configs.
 */

export interface DevActor {
  id: string;
  name: string;
  email: string;
  role: string;
}

/**
 * Dev actor personas keyed by auth ID.
 * Used by AuthContext for mock auth and by ceremony defaults for role resolution.
 *
 * Role IDs must match data/configs/role-registry.yaml
 */
export const DEV_ACTOR_MAP: Record<string, { name: string; email: string; role: string }> = {
  // ── FERP System (tenant 0) ─────────────────────────────────────────────────
  '0_1':                    { name: 'System Admin',           email: 'admin@ferp.system',            role: 'admin' },

  // ── FERP Operations (tenant 1) ─────────────────────────────────────────────
  '1_1':                    { name: 'Operations Admin',       email: 'admin@ferp.operations',        role: 'admin' },

  // ── Hillside Harmony (ALF / 144G, tenant 2) ────────────────────────────────
  // MUST match data/tenants/hillside-harmony/actors.yaml (DRR-303)
  // DRR-194: Role IDs from data/configs/roles/fhir-1.yaml (scope-encoded per DRR-089)
  // DRR-220: Sequential actor IDs matching actors.yaml (2_N format)
  '2_1':                    { name: 'Admin User',             email: 'admin@hillsideharmony.com',    role: 'role-4-0-1' },  // ALD
  '2_2':                    { name: 'Bao Vang',               email: 'don@hillsideharmony.com',      role: 'role-4-0-2' },  // DoN (CNS-oversight)
  '2_3':                    { name: 'Der Vang',               email: 'der@hillsideharmony.com',      role: 'role-4-0-5' },  // Med Aide (ULP-delegated)
  '2_4':                    { name: 'Xee Vang',               email: 'xee@hillsideharmony.com',      role: 'role-4-0-5' },  // Med Aide (ULP-delegated)
  '2_5':                    { name: 'Chanhsuk Vang',          email: 'chanhsuk@hillsideharmony.com', role: 'role-4-0-5' },  // Med Aide (ULP-delegated)
  '2_6':                    { name: 'Manee Xiong',            email: 'manee@hillsideharmony.com',    role: 'role-4-0-5' },  // Med Aide (ULP-delegated)
  '2_7':                    { name: 'Pai Yang',               email: 'pai@hillsideharmony.com',      role: 'role-4-0-5' },  // Med Aide (ULP-delegated)
  '2_8':                    { name: 'Xhu Vang',               email: 'xhu@hillsideharmony.com',      role: 'role-4-0-5' },  // Med Aide (ULP-delegated)
  '2_9':                    { name: 'Pany Thao',              email: 'pany@hillsideharmony.com',     role: 'role-4-0-5' },  // Med Aide (ULP-delegated)
  '2_10':                   { name: 'Chelsea Thao',           email: 'chelsea@hillsideharmony.com',  role: 'role-4-0-5' },  // Med Aide (ULP-delegated)
  '2_11':                   { name: 'Bee Yang',               email: 'bee@hillsideharmony.com',      role: 'role-4-0-4' },  // ULP-basic
  '2_12':                   { name: 'Bao Vang',               email: 'bao2@hillsideharmony.com',     role: 'role-4-0-4' },  // ULP-basic
  // ── Hillside Harmony — Composite Actors (not for impersonation) ────────────
  // Note: 2_20, 2_21 are org/facility composites per actors.yaml

  // ── Athanor Assisted Living, LLC (ALF / 144G, tenant 3) ────────────────────
  '3_1':                    { name: 'Athanor Admin',          email: 'admin@athanor-alf.com',        role: 'role-4-0-1' },  // ALD
  '3_2':                    { name: 'Athanor DoN',            email: 'don@athanor-alf.com',          role: 'role-4-0-2' },  // DoN

  // ── Omniquark, LLC (Professional Services, tenant 4) ───────────────────────
  '4_1':                    { name: 'Phin Vang',              email: 'phin@omniquark.com',           role: 'role-0-0-2' },
  '4_2':                    { name: 'Developer',              email: 'dev@omniquark.com',            role: 'role-0-0-1' },

  // ── Culturally Conscious Kitchen (Food Service, tenant 5) ──────────────────
  '5_1':                    { name: 'Kitchen Admin',          email: 'admin@cc-kitchen.com',         role: 'role-0-0-2' },
  '5_2':                    { name: 'Kitchen Manager',        email: 'manager@cc-kitchen.com',       role: 'operational-qa' },

  // ── Culturally Conscious Laundry (Laundry Service, tenant 6) ───────────────
  '6_1':                    { name: 'Laundry Admin',          email: 'admin@cc-laundry.com',         role: 'role-0-0-2' },
  '6_2':                    { name: 'Laundry Manager',        email: 'manager@cc-laundry.com',       role: 'operational-qa' },

  // ── AAHHCS (Home Care / CFSS, tenant 7) ────────────────────────────────────
  '7_1':                    { name: 'Phin Vang',              email: 'phin@aahhcs.com',              role: 'role-0-0-2' },
  '7_2':                    { name: 'Mai Lee',                email: 'mai@aahhcs.com',               role: 'care-coordinator' },
  '7_3':                    { name: 'Fatima Hassan',          email: 'fatima@aahhcs.com',            role: 'pca-home-care' },
  '7_4':                    { name: 'Sarah Johnson',          email: 'sarah@aahhcs.com',             role: 'financial-analyst' },
  '7_5':                    { name: 'David Chen',             email: 'david@aahhcs.com',             role: 'operational-qa' },

  // ── Athanor Beauty & Wellness (tenant 21) ──────────────────────────────────
  '21_1':                   { name: 'Wellness Admin',         email: 'admin@athanor-wellness.com',   role: 'role-0-0-2' },

  // ── Athanor Celebration of Life (tenant 22) ────────────────────────────────
  '22_1':                   { name: 'Memorial Admin',         email: 'admin@athanor-memorial.com',   role: 'role-0-0-2' },

  // ── Phin Personal Finance (tenant 23) ──────────────────────────────────────
  '23_1':                   { name: 'Phin Vang',              email: 'phin@personal.ferp.local',     role: 'role-0-0-2' },
};

/**
 * Ceremony signing role → compatible DEV_ACTOR_MAP roles.
 * Derived from wf-4-0-109 (care plan signing ceremony) + MN 144G requirements.
 *
 * DRR-201: All role IDs must match role-registry.yaml
 */
const CEREMONY_ROLE_TO_ACTOR_ROLES: Record<string, string[]> = {
  // DRR-089: Role IDs match data/configs/roles/fhir-1.yaml (scope-encoded)
  resident_or_representative: ['role-4-0-10', 'role-4-0-11', 'role-4-0-12'],  // client roles (TBD)
  designated_coordinator: ['role-4-0-9', 'role-4-0-2'],  // QP, DoN
  licensed_nurse: ['role-4-0-3', 'role-4-0-2', 'role-4-0-6'],  // CNS-bedside, DoN, LPN
  witness: ['role-4-0-8', 'role-0-0-1'],  // QA, admin
  approver: ['role-4-0-1', 'role-0-0-2', 'role-4-0-2'],  // ALD, architect, DoN
  author: ['role-4-0-1', 'role-0-0-2', 'role-4-0-9', 'role-4-0-6', 'role-4-0-8', 'role-4-0-7'],  // ALD, architect, QP, LPN, QA, billing
};

/**
 * Get all dev actors whose role is compatible with a ceremony signing role.
 */
export function getActorsForCeremonyRole(ceremonyRole: string): DevActor[] {
  const compatibleRoles = CEREMONY_ROLE_TO_ACTOR_ROLES[ceremonyRole] ?? [];
  if (compatibleRoles.length === 0) return [];

  return Object.entries(DEV_ACTOR_MAP)
    .filter(([, actor]) => compatibleRoles.includes(actor.role))
    .map(([id, actor]) => ({ id, ...actor }));
}

/**
 * Get all dev actors as a flat list.
 */
export function getAllDevActors(): DevActor[] {
  return Object.entries(DEV_ACTOR_MAP).map(([id, actor]) => ({ id, ...actor }));
}
